/*
  Engraving engine for the photo-engraved coin necklace (2026-09-30). No UI here: gravur.js drives it.

  1. yerlestir(): the customer's image is placed into an S × S square that covers the engraving circle
     (cover-fit, then the customer's zoom, rotation and offset) and turned into luminance 0..1.
  2. One of three styles turns that into an engraving map E (0 = polished metal, 1 = engraved):
     - cizgi  (Line art): XDoG, a difference-of-Gaussians edge drawing with soft ink fill in the darkest areas
     - foto   (Photo):   levels + sharpening + Floyd–Steinberg dithering, like a laser photo engraving
     - damga  (Stamp):   Otsu threshold for drawings, logos, text and memes (colourful strokes count as ink)
  3. isle() renders E onto the blank coin photo: the engraved parts become the matte, slightly darker and warmer
     tone measured on a real laser-engraved Enos coin (gold ≈ polished × [0.865, 0.806, 0.81]); the engraved surface
     loses the polished reflections, gets a fine laser grain, and every edge a 1 px wall — shadow on the side facing
     the light (top left), a highlight on the opposite side.
  4. lazer(): the production file — black = engrave, white = leave polished — clipped to the circle.
*/
(() => {
  if (window.GravurMotor) return;

  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const lerp = (a, b, t) => a + (b - a) * t;

  // Gaussian blur approximated by three box blurs (separable, O(n) whatever the radius)
  function kutular(sigma, n = 3) {
    const wIdeal = Math.sqrt((12 * sigma * sigma) / n + 1);
    let wl = Math.floor(wIdeal); if (wl % 2 === 0) wl--;
    const wu = wl + 2;
    const m = Math.round((12 * sigma * sigma - n * wl * wl - 4 * n * wl - 3 * n) / (-4 * wl - 4));
    return Array.from({ length: n }, (_, i) => (i < m ? wl : wu));
  }
  function kutuYatay(src, dst, w, h, r) {
    const iarr = 1 / (r + r + 1);
    for (let i = 0; i < h; i++) {
      let ti = i * w, li = ti, ri = ti + r;
      const fv = src[ti], lv = src[ti + w - 1];
      let val = (r + 1) * fv;
      for (let j = 0; j < r; j++) val += src[ti + Math.min(j, w - 1)];
      for (let j = 0; j <= r; j++) { val += src[Math.min(ri++, ti + w - 1)] - fv; dst[ti++] = val * iarr; }
      for (let j = r + 1; j < w - r; j++) { val += src[ri++] - src[li++]; dst[ti++] = val * iarr; }
      for (let j = w - r; j < w; j++) { val += lv - src[li++]; dst[ti++] = val * iarr; }
    }
  }
  function kutuDikey(src, dst, w, h, r) {
    const iarr = 1 / (r + r + 1);
    for (let i = 0; i < w; i++) {
      let ti = i, li = ti, ri = ti + r * w;
      const fv = src[ti], lv = src[ti + w * (h - 1)];
      let val = (r + 1) * fv;
      for (let j = 0; j < r; j++) val += src[ti + Math.min(j, h - 1) * w];
      for (let j = 0; j <= r; j++) { val += src[Math.min(ri, ti + w * (h - 1))] - fv; dst[ti] = val * iarr; ri += w; ti += w; }
      for (let j = r + 1; j < h - r; j++) { val += src[ri] - src[li]; dst[ti] = val * iarr; li += w; ri += w; ti += w; }
      for (let j = h - r; j < h; j++) { val += lv - src[li]; dst[ti] = val * iarr; li += w; ti += w; }
    }
  }
  function bulanik(src, w, h, sigma) {
    if (sigma < 0.3) return Float32Array.from(src);
    let a = Float32Array.from(src), b = new Float32Array(src.length);
    for (const k of kutular(sigma)) {
      const r = (k - 1) / 2;
      kutuYatay(a, b, w, h, r); kutuDikey(b, a, w, h, r);
    }
    return a;
  }

  // luminance percentiles inside the circle → stretch to 0..1
  function seviye(L, S, alt = 0.01, ust = 0.99) {
    const hist = new Uint32Array(256); let n = 0;
    const c = (S - 1) / 2, r2 = (S / 2) * (S / 2);
    for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
      if ((x - c) * (x - c) + (y - c) * (y - c) > r2) continue;
      hist[clamp(Math.round(L[y * S + x] * 255), 0, 255)]++; n++;
    }
    const bul = (p) => { let t = 0; for (let i = 0; i < 256; i++) { t += hist[i]; if (t >= p * n) return i / 255; } return 1; };
    const lo = bul(alt), hi = Math.max(bul(ust), lo + 0.05);
    const out = new Float32Array(L.length);
    for (let i = 0; i < L.length; i++) out[i] = clamp((L[i] - lo) / (hi - lo), 0, 1);
    return out;
  }

  function otsu(L, S) {
    const hist = new Float64Array(256); let n = 0;
    const c = (S - 1) / 2, r2 = (S / 2) * (S / 2);
    for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
      if ((x - c) * (x - c) + (y - c) * (y - c) > r2) continue;
      hist[clamp(Math.round(L[y * S + x] * 255), 0, 255)]++; n++;
    }
    let top = 0; for (let i = 0; i < 256; i++) top += i * hist[i];
    let wB = 0, sumB = 0, enIyi = 0, esik = 127;
    for (let i = 0; i < 256; i++) {
      wB += hist[i]; if (!wB) continue;
      const wF = n - wB; if (!wF) break;
      sumB += i * hist[i];
      const mB = sumB / wB, mF = (top - sumB) / wF, v = wB * wF * (mB - mF) * (mB - mF);
      if (v > enIyi) { enIyi = v; esik = i; }
    }
    return esik / 255;
  }

  const GravurMotor = {
    // Place the image into S × S. o = { olcek ≥ 1, dx, dy (fractions of S), aci (degrees) }.
    // Returns { L: luminance, D: "ink" (luminance pulled down by colour, for drawings) }.
    yerlestir(kaynak, S, o = {}) {
      const cv = document.createElement('canvas'); cv.width = cv.height = S;
      const ctx = cv.getContext('2d', { willReadFrequently: true });
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, S, S);
      const w = kaynak.width, h = kaynak.height;
      const k = Math.max(S / w, S / h) * (o.olcek || 1);
      ctx.translate(S / 2 + (o.dx || 0) * S, S / 2 + (o.dy || 0) * S);
      ctx.rotate(((o.aci || 0) * Math.PI) / 180);
      ctx.scale(k, k);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(kaynak, -w / 2, -h / 2);
      const d = ctx.getImageData(0, 0, S, S).data;
      const L = new Float32Array(S * S), D = new Float32Array(S * S);
      for (let i = 0, j = 0; i < L.length; i++, j += 4) {
        const r = d[j] / 255, g = d[j + 1] / 255, b = d[j + 2] / 255;
        const l = 0.299 * r + 0.587 * g + 0.114 * b;
        const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
        const sat = mx > 0 ? (mx - mn) / mx : 0;
        L[i] = l; D[i] = clamp(l - 0.55 * sat * mx, 0, 1);
      }
      return { L, D };
    },

    // Line art. detay 0..1 (more = finer lines and more of them).
    cizgi(L, S, detay = 0.5) {
      const olc = S / 512;
      const A = seviye(bulanik(L, S, S, lerp(1.6, 0.6, detay) * olc), S);
      const sigma = lerp(2.6, 0.9, detay) * olc;
      const g1 = bulanik(A, S, S, sigma), g2 = bulanik(A, S, S, sigma * 1.6);
      const p = 22, eps = lerp(0.52, 0.64, detay), phi = 18;
      const E = new Float32Array(S * S);
      for (let i = 0; i < E.length; i++) {
        const D = (1 + p) * g1[i] - p * g2[i];
        const acik = D >= eps ? 1 : 1 + Math.tanh(phi * (D - eps));
        E[i] = clamp(1 - acik, 0, 1);
      }
      return E;
    },

    // Photo: dithered like a laser photo engraving. The dot grid is DS × DS (≈ real dot size on a 20 mm coin).
    foto(L, S, detay = 0.5, DS = 380) {
      const olc = S / 512;
      let A = seviye(L, S, 0.005, 0.995);
      const bl = bulanik(A, S, S, 1.4 * olc);
      const guc = lerp(0.4, 1.4, detay);
      const kontrast = lerp(1.15, 1.6, detay);
      for (let i = 0; i < A.length; i++) A[i] = clamp((A[i] + guc * (A[i] - bl[i]) - 0.5) * kontrast + 0.5, 0, 1);
      // resample to the dot grid
      const n = Math.min(DS, S), B = new Float32Array(n * n);
      for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
        const sx = Math.min(S - 1, Math.floor(((x + 0.5) * S) / n)), sy = Math.min(S - 1, Math.floor(((y + 0.5) * S) / n));
        B[y * n + x] = Math.pow(A[sy * S + sx], 0.9);
      }
      // Floyd–Steinberg, serpentine; engrave the dark
      for (let y = 0; y < n; y++) {
        const ters = y & 1;
        for (let k = 0; k < n; k++) {
          const x = ters ? n - 1 - k : k, i = y * n + x;
          const eski = B[i], yeni = eski < 0.5 ? 0 : 1, hata = eski - yeni;
          B[i] = yeni;
          const s = ters ? -1 : 1;
          if (x + s >= 0 && x + s < n) B[i + s] += (hata * 7) / 16;
          if (y + 1 < n) {
            if (x - s >= 0 && x - s < n) B[i + n - s] += (hata * 3) / 16;
            B[i + n] += (hata * 5) / 16;
            if (x + s >= 0 && x + s < n) B[i + n + s] += hata / 16;
          }
        }
      }
      const E = new Float32Array(n * n);
      for (let i = 0; i < E.length; i++) E[i] = 1 - B[i];
      E.boyut = n;
      return E;
    },

    // Stamp: bold two-tone for drawings, logos, text and memes.
    damga(D, S, detay = 0.5) {
      const olc = S / 512;
      const A = bulanik(D, S, S, 0.7 * olc);
      const t = clamp(otsu(A, S) + (detay - 0.5) * 0.3, 0.05, 0.95), yumu = 0.035;
      const E = new Float32Array(S * S);
      for (let i = 0; i < E.length; i++) E[i] = clamp((t - A[i]) / yumu + 0.5, 0, 1);
      return E;
    },

    ters(E) { const o = new Float32Array(E.length); for (let i = 0; i < E.length; i++) o[i] = 1 - E[i]; o.boyut = E.boyut; return o; },

    // Sample E (n × n, covering the circle's bounding square) at fractional coords u, v in 0..1
    ornek(E, u, v) {
      const n = E.boyut || Math.round(Math.sqrt(E.length));
      const x = u * n - 0.5, y = v * n - 0.5;
      const x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0;
      const g = (xx, yy) => (xx < 0 || yy < 0 || xx >= n || yy >= n ? 0 : E[yy * n + xx]);
      return (g(x0, y0) * (1 - fx) + g(x0 + 1, y0) * fx) * (1 - fy) + (g(x0, y0 + 1) * (1 - fx) + g(x0 + 1, y0 + 1) * fx) * fy;
    },

    // Metal looks of the engraved (laser-matted) surface, as multipliers of the polished colour.
    METAL: {
      gold: { carp: [0.83, 0.775, 0.78], duz: 0.5, tane: 11 },
      silver: { carp: [0.74, 0.74, 0.76], duz: 0.5, tane: 11 },
      rose: { carp: [0.84, 0.76, 0.75], duz: 0.5, tane: 11 },
    },

    // Render E onto the coin in ImageData `img` (the whole photo or a crop). yer = { cx, cy, r } in img pixels:
    // the engraving circle. Pixels outside are left untouched.
    isle(img, yer, E, metal = 'gold') {
      const M = this.METAL[metal] || this.METAL.gold;
      const { width: W, height: H, data } = img;
      // yer: centre + radii of the engraving ellipse (a circle seen at an angle), aci = tilt of the ellipse,
      // donus = rotation of the engraving inside the coin (degrees). A plain { cx, cy, r } is a front-facing circle.
      const { cx, cy } = yer, rx = yer.rx ?? yer.r, ry = yer.ry ?? yer.rx ?? yer.r;
      const a = ((yer.aci || 0) * Math.PI) / 180, dn = ((yer.donus || 0) * Math.PI) / 180;
      const ca = Math.cos(a), sa = Math.sin(a), cd = Math.cos(dn), sd = Math.sin(dn);
      const yw = Math.sqrt(rx * rx * ca * ca + ry * ry * sa * sa), yh = Math.sqrt(rx * rx * sa * sa + ry * ry * ca * ca);
      const x0 = Math.max(0, Math.floor(cx - yw - 4)), x1 = Math.min(W - 1, Math.ceil(cx + yw + 4));
      const y0 = Math.max(0, Math.floor(cy - yh - 4)), y1 = Math.min(H - 1, Math.ceil(cy + yh + 4));
      const gw = x1 - x0 + 1, gh = y1 - y0 + 1, rMin = Math.min(rx, ry);
      // the engraving map on the pixel grid (anti-aliased edge)
      const G = new Float32Array(gw * gh);
      let ort = [0, 0, 0], n = 0;
      for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
        const dx = x - cx, dy = y - cy;
        const nu = (dx * ca + dy * sa) / rx, nv = (-dx * sa + dy * ca) / ry;
        const d = Math.sqrt(nu * nu + nv * nv);
        if (d > 1 + 1 / rMin) continue;
        const su = nu * cd + nv * sd, sv = -nu * sd + nv * cd;
        G[(y - y0) * gw + (x - x0)] = clamp((1 - d) * rMin + 0.5, 0, 1) * this.ornek(E, su / 2 + 0.5, sv / 2 + 0.5);
        if ((x & 3) === 0 && (y & 3) === 0) { const j = (y * W + x) * 4; ort[0] += data[j]; ort[1] += data[j + 1]; ort[2] += data[j + 2]; n++; }
      }
      ort = ort.map((v) => v / Math.max(1, n));
      const r = rMin;
      // the engraving's wall: shadow on the edge facing the light (top left), highlight on the opposite edge
      const b = Math.max(1, Math.round(r / 150));
      const g = (x, y) => (x < 0 || y < 0 || x >= gw || y >= gh ? 0 : G[y * gw + x]);
      for (let y = 0; y < gh; y++) for (let x = 0; x < gw; x++) {
        const v = G[y * gw + x], vUL = g(x - b, y - b), vDR = g(x + b, y + b);
        if (v <= 0.002 && vUL <= 0.002 && vDR <= 0.002) continue;
        const X = x + x0, Y = y + y0, j = (Y * W + X) * 4;
        let hsh = (X * 374761393 + Y * 668265263) | 0; hsh = (hsh ^ (hsh >>> 13)) * 1274126177; hsh ^= hsh >>> 16;
        const tane = ((hsh & 1023) / 1023 - 0.5) * 2 * M.tane;
        const golge = Math.max(0, v - vUL), isik = Math.max(0, v - vDR);
        for (let c = 0; c < 3; c++) {
          const bb = data[j + c];
          const mat = lerp(bb, ort[c], M.duz) * M.carp[c] + tane;
          let o = bb * (1 - v) + mat * v;
          o = o * (1 - 0.5 * golge) + 34 * isik;
          data[j + c] = o;
        }
      }
      return img;
    },

    // Production file: black = engrave, white = leave polished; circle of diameter N.
    lazer(E, N = 2000) {
      const cv = document.createElement('canvas'); cv.width = cv.height = N;
      const ctx = cv.getContext('2d');
      const im = ctx.createImageData(N, N), d = im.data, R = N / 2;
      for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
        const j = (y * N + x) * 4;
        const icinde = (x + 0.5 - R) ** 2 + (y + 0.5 - R) ** 2 <= R * R;
        const v = icinde ? this.ornek(E, (x + 0.5) / N, (y + 0.5) / N) : 0;
        const g = Math.round(255 * (1 - v));
        d[j] = d[j + 1] = d[j + 2] = g; d[j + 3] = 255;
      }
      ctx.putImageData(im, 0, 0);
      return cv;
    },
  };

  window.GravurMotor = GravurMotor;
})();
